import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import * as Sentry from "@sentry/react";
import { tabSessionId } from "./services/tabSession";

// CSS
import "./styles/style.scss";

// captureConsoleIntegration joins console.* arguments into the event title with a naive string
// coercion, so an object argument (e.g. an Apisauce response) collapses to "[object Object]".
// Serialize each argument (circular-safe, length-capped) so object contents are readable.
const stringifyConsoleArg = (arg) => {
  if (typeof arg === "string") return arg;
  if (arg instanceof Error) return arg.message || String(arg);
  try {
    const seen = new WeakSet();
    const json = JSON.stringify(arg, (_key, value) => {
      if (typeof value === "object" && value !== null) {
        if (seen.has(value)) return "[Circular]";
        seen.add(value);
      }
      return value;
    });
    if (json && json !== "{}" && json !== "[]") {
      return json.length > 2000 ? json.slice(0, 2000) + "…" : json;
    }
  } catch {
    // fall through to String() below
  }
  try {
    return String(arg);
  } catch {
    return "[Unserializable]";
  }
};

// Rewrite the title of console-captured events (which carry the raw args in extra.arguments) so a
// trailing "[object Object]" becomes the serialized object. Non-console events are left untouched.
const rebuildConsoleMessage = (event) => {
  const args = event.extra?.arguments;
  if (!Array.isArray(args) || args.length === 0) return event;
  const readable = args.map(stringifyConsoleArg).join(" ").trim();
  if (!readable) return event;
  if (event.message) event.message = readable;
  event.exception?.values?.forEach((ex) => {
    if (typeof ex.value === "string" && ex.value.includes("[object Object]")) {
      ex.value = readable;
    }
  });
  return event;
};

if (import.meta.env.VITE_SENTRY_ENABLED === "true") {
  Sentry.init({
    // This app's OWN Sentry frontend project DSN (org teamwork-commerce). Replace with
    // the new project's DSN — never inherit another app's.
    dsn: "https://9c001ed6793bf6ac4ee91661e081b68c@o4511622206849024.ingest.us.sentry.io/4512009681829888",
    initialScope: {
      tags: { tab_session: tabSessionId },
    },
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    enableLogs: true,
    environment: import.meta.env.VITE_SENTRY_ENV,
    attachStacktrace: true,
    normalizeDepth: 3,
    integrations: [
      Sentry.browserSessionIntegration(),
      Sentry.contextLinesIntegration(),
      Sentry.extraErrorDataIntegration(),
      Sentry.functionToStringIntegration(),
      Sentry.httpClientIntegration(),
      Sentry.linkedErrorsIntegration(),
      Sentry.browserTracingIntegration(),
      Sentry.captureConsoleIntegration({
        levels: "error",
      }),
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    // Make object arguments in console.error(...) readable instead of "[object Object]".
    beforeSend(event) {
      const next = rebuildConsoleMessage(event);
      // Group every event from the same tab/session into one Sentry issue, regardless of the
      // individual error's type, message, or stack trace. Overrides Sentry's default fingerprint.
      next.fingerprint = [tabSessionId];
      return next;
    },
    tracesSampleRate: 1.0,
    // Propagate the sentry-trace/baggage headers to our API (VITE_URL_API is
    // matched as a substring of each outgoing request URL) so the browser
    // event, the API event, and any downstream API event share one trace_id
    // and connect across Sentry projects into a single trace.
    tracePropagationTargets: ["localhost", import.meta.env.VITE_URL_API],
    // Set profilesSampleRate to 1.0 to profile every transaction.
    // Since profilesSampleRate is relative to tracesSampleRate,
    // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
    // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
    // results in 25% of transactions being profiled (0.5*0.5=0.25)
    profilesSampleRate: 1.0,
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(<App />);
